import {
  GenerationDebug,
  IDungeonGenerationOptions,
  IDungeonGenerationResult,
  IRoom,
  ITile,
  PlacedLocation,
  RoomTemplate,
} from '../../types/map.types';
import { IPosition } from '../../types/common.types';
import { LOCATION_REGISTRY } from '../../data/locations.config';
import { fromPattern, makeRect } from './room-shapes';
import { placeRooms, PlacedRoom, toRoom } from './placement';
import { buildMST } from './graph';
import { carveCorridors } from './corridors';
import {
  enforceBorder,
  removeCorridorBlockingWalls,
  removeDisconnectedRegions,
  removeTinyWallPockets,
} from './integrity';
import { placeLocations, placeTemplateLocations } from './locations';

type Grid = number[][];

function createGrid(width: number, height: number): Grid {
  const grid: Grid = [];
  for (let y = 0; y < height; y++) {
    grid[y] = new Array<number>(width).fill(0);
  }
  return grid;
}

function templateToShape(t: RoomTemplate) {
  const shape = t.pattern
    ? fromPattern(t.pattern, t.width, t.height)
    : makeRect(t.width, t.height);
  return { id: t.id, shape, padding: t.padding ?? 1 };
}

function stampRoom(grid: Grid, room: PlacedRoom): void {
  for (const c of room.cells) {
    grid[c.y][c.x] = 1;
  }
}

function gridToTiles(grid: Grid, width: number, height: number): ITile[] {
  const tiles: ITile[] = [];
  let id = 0;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      tiles.push({
        id: id++,
        x,
        y,
        type: grid[y][x] === 1 ? 'ground' : 'wall',
      });
    }
  }
  return tiles;
}

function recomputeCellsFromGrid(rooms: PlacedRoom[], grid: Grid): void {
  // Post-process can shave a few room cells (border, pocket fill). Drop cells
  // that are no longer ground so IRoom.cells matches the final grid exactly.
  for (const room of rooms) {
    room.cells = room.cells.filter((c) => grid[c.y][c.x] === 1);
  }
}

export function generateDungeon(
  opts: IDungeonGenerationOptions & { roomCount?: [number, number] },
): IDungeonGenerationResult {
  const { width, height, style, locations = [], roomTemplates = [] } = opts;
  const [minRooms, maxRooms] = opts.roomCount ?? [3, 5];
  const targetCount =
    minRooms + Math.floor(Math.random() * (maxRooms - minRooms + 1));

  const templateShapes = roomTemplates.map(templateToShape);
  const placed = placeRooms(width, height, style, targetCount, templateShapes);

  const grid = createGrid(width, height);
  for (const room of placed) {
    stampRoom(grid, room);
  }

  const pairs = buildMST(placed);
  carveCorridors(grid, placed, pairs, style);

  enforceBorder(grid, 1);
  removeCorridorBlockingWalls(grid);

  // Re-stamp before flood-fill: a template room re-stamped after removeDisconnectedRegions
  // could resurrect a genuinely unreachable room. Stamping first lets the flood-fill erase
  // it if it is disconnected. removeTinyWallPockets only adds ground, so templates survive it.
  for (const room of placed) {
    if (room.templateId) {
      stampRoom(grid, room);
    }
  }

  const seed: IPosition = placed[0]?.center ?? { x: 1, y: 1 };
  removeDisconnectedRegions(grid, seed);
  removeTinyWallPockets(grid);

  recomputeCellsFromGrid(placed, grid);

  const survivors = placed.filter((r) => r.cells.length > 0);
  const rooms: IRoom[] = survivors.map(toRoom);

  const claimed = new Set<string>();
  const templatedRoomIndices = new Set<number>();
  survivors.forEach((r, i) => {
    if (r.templateId) {
      templatedRoomIndices.add(i);
    }
  });

  const templateLocations = placeTemplateLocations(
    survivors,
    roomTemplates,
    claimed,
  );
  const proceduralLocations = placeLocations(
    grid,
    survivors,
    templatedRoomIndices,
    locations,
    claimed,
  );
  const placedLocations: PlacedLocation[] = [
    ...templateLocations,
    ...proceduralLocations,
  ];

  const tiles = gridToTiles(grid, width, height);

  const debug: GenerationDebug = {
    style,
    width,
    height,
    targetCount,
    placedCount: placed.length,
    droppedCount: placed.length - survivors.length,
    corridorCount: pairs.length,
    rooms: survivors.map((r, i) => ({
      index: i,
      templateId: r.templateId,
      family: r.family,
      bbox: { x: r.x, y: r.y, width: r.shape.width, height: r.shape.height },
      cellCount: r.cells.length,
      center: r.center,
    })),
  };

  return { tiles, width, height, rooms, placedLocations, debug };
}

export function generateTestArena(
  width: number,
  height: number,
): IDungeonGenerationResult {
  const tiles: ITile[] = [];
  let id = 0;
  const cells: IPosition[] = [];
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const isBorder =
        x === 0 || y === 0 || x === width - 1 || y === height - 1;
      tiles.push({ id: id++, x, y, type: isBorder ? 'wall' : 'ground' });
      if (!isBorder) {
        cells.push({ x, y });
      }
    }
  }
  const room: IRoom = {
    x: 1,
    y: 1,
    width: width - 2,
    height: height - 2,
    left: 1,
    right: width - 2,
    top: 1,
    bottom: height - 2,
    cells,
  };
  const placedLocations: PlacedLocation[] = [];
  const vaultDef = LOCATION_REGISTRY['vault'];
  if (vaultDef) {
    placedLocations.push({
      id: vaultDef.id,
      x: Math.floor(width / 2) + 3,
      y: Math.floor(height / 2),
      width: vaultDef.width,
      height: vaultDef.height,
      placement: vaultDef.placement,
      roomIndex: 0,
      sprite: vaultDef.sprite,
      interactionType: vaultDef.interactionType,
      spriteAnimationSpeed: vaultDef.spriteAnimationSpeed,
    });
  }
  return { tiles, width, height, rooms: [room], placedLocations };
}
